import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import { api, errMsg, useFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, ErrorState, EmptyState, Pagination, Field, Confirm } from "@/components/common";
import { DashHeader } from "@/dashboard/DashboardLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ImageField } from "@/components/ImageField";
import { NEWS_CAT, fmtDateTime, toLocalInput, fromLocalInput } from "@/lib/format";

const NEWS_STATUS = { draft: "Bozza", published: "Pubblicata", scheduled: "Programmata", archived: "Archiviata" };

function NewsDialog({ item, onClose, onSaved, canPublish }) {
  const [f, setF] = useState(item ? { ...item, image_url: item.image_url || "", publish_at: toLocalInput(item.publish_at) } : { title: "", excerpt: "", content: "", image_url: "", category: "annunci", status: "draft", publish_at: "", featured: false });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const payload = { title: f.title, excerpt: f.excerpt, content: f.content, image_url: f.image_url || null, category: f.category, status: f.status, publish_at: fromLocalInput(f.publish_at), featured: f.featured };
    try {
      if (item) await api.put(`/admin/news/${item.id}`, payload);
      else await api.post("/admin/news", payload);
      toast.success("News salvata");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0B0B0F] border-[#C6C6CE]/15 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="news-form-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase tracking-wide">{item ? "Modifica news" : "Nuova news"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Titolo"><input data-testid="news-title-input" required minLength={3} className="nx-input" value={f.title} onChange={set("title")} /></Field>
          <Field label="Estratto"><input data-testid="news-excerpt-input" className="nx-input" value={f.excerpt} onChange={set("excerpt")} /></Field>
          <Field label="Testo"><textarea data-testid="news-content-input" required rows={8} className="nx-input" value={f.content} onChange={set("content")} /></Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <ImageField label="Immagine" testId="news-image" value={f.image_url} onChange={(v) => setF({ ...f, image_url: v })} />
            <Field label="Categoria"><select data-testid="news-category-select" className="nx-input" value={f.category} onChange={set("category")}>{Object.entries(NEWS_CAT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
            <Field label="Stato"><select data-testid="news-status-select" className="nx-input" value={f.status} onChange={set("status")}>{Object.entries(NEWS_STATUS).filter(([k]) => canPublish || k === "draft").map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
            <Field label="Data pubblicazione (per programmate)"><input data-testid="news-publish-at-input" type="datetime-local" className="nx-input" value={f.publish_at} onChange={set("publish_at")} /></Field>
          </div>
          {canPublish && <label className="flex items-center gap-2 text-sm"><input data-testid="news-featured-checkbox" type="checkbox" checked={f.featured} onChange={set("featured")} /> In evidenza in homepage</label>}
          <div className="flex justify-end gap-2"><button type="button" onClick={onClose} className="btn-ghost btn-sm">Annulla</button><button data-testid="news-form-submit" className="btn-primary btn-sm">Salva</button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function AdminNews() {
  const { user } = useAuth();
  const canPublish = user.role === "super_admin" || user.can_publish_news;
  const [f, setF] = useState({ page: 1 });
  const [editing, setEditing] = useState(undefined);
  const { data, loading, error, reload } = useFetch("/admin/news", f);
  const archive = async (n) => {
    try { await api.put(`/admin/news/${n.id}`, { ...n, status: "archived" }); toast.success("News archiviata"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="dash-news">
      <DashHeader title="News e comunicazioni" subtitle={canPublish ? "Bozze, pubblicazione programmata e archiviazione." : "Puoi creare bozze; la pubblicazione richiede l'autorizzazione del Super Admin."}>
        <button data-testid="new-news-button" onClick={() => setEditing(null)} className="btn-primary btn-sm"><Plus className="w-4 h-4" /> Nuova news</button>
      </DashHeader>
      <select data-testid="admin-news-status" aria-label="Stato" className="nx-input w-auto mb-5" onChange={(e) => setF({ ...f, status: e.target.value || undefined, page: 1 })}><option value="">Tutti gli stati</option>{Object.entries(NEWS_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
      {error ? <ErrorState message={error} onRetry={reload} /> : loading && !data ? <Loader /> : data.items.length ? (
        <div className="nx-card divide-y divide-[#C6C6CE]/5">
          {data.items.map((n) => (
            <div key={n.id} className="p-4 flex flex-wrap items-center gap-4" data-testid={`admin-news-row-${n.slug}`}>
              <div className="flex-1 min-w-[200px]"><div className="text-[#F5F5F7]">{n.title} {n.featured && <span className="text-[10px] text-[#962FFF] uppercase tracking-widest ml-2">In evidenza</span>}</div><div className="text-xs text-[#C6C6CE]/50">{NEWS_CAT[n.category]} · {n.author_name} · {fmtDateTime(n.publish_at || n.created_at)}</div></div>
              <span className="text-[10px] uppercase tracking-widest text-[#C6C6CE]/70">{NEWS_STATUS[n.status]}</span>
              {n.status === "published" && <Link to={`/news/${n.slug}`} className="text-xs text-[#962FFF] hover:underline">Vedi</Link>}
              <button data-testid={`edit-news-${n.slug}`} onClick={() => setEditing(n)} className="btn-ghost btn-sm"><Pencil className="w-3.5 h-3.5" /></button>
              {canPublish && n.status !== "archived" && <Confirm testId={`archive-news-${n.slug}`} trigger={<button data-testid={`archive-news-btn-${n.slug}`} className="btn-danger">Archivia</button>} title="Archiviare la news?" description="Non sarà più visibile pubblicamente." onConfirm={() => archive(n)} />}
            </div>
          ))}
          <div className="p-3"><Pagination page={data.page} pages={data.pages} onChange={(p) => setF({ ...f, page: p })} /></div>
        </div>
      ) : <EmptyState title="Nessuna news" />}
      {editing !== undefined && <NewsDialog item={editing} canPublish={canPublish} onClose={() => setEditing(undefined)} onSaved={reload} />}
    </div>
  );
}

function RegulationDialog({ item, onClose, onSaved }) {
  const [f, setF] = useState(item || { title: "", category: "generale", content: "", version: "1.0", status: "published" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const payload = { title: f.title, category: f.category, content: f.content, version: f.version, status: f.status };
    try {
      if (item) await api.put(`/admin/regulations/${item.id}`, payload);
      else await api.post("/admin/regulations", payload);
      toast.success("Regolamento salvato");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0B0B0F] border-[#C6C6CE]/15 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="regulation-form-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase tracking-wide">{item ? "Modifica regolamento" : "Nuovo regolamento"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Titolo"><input data-testid="regulation-title-input" required minLength={3} className="nx-input" value={f.title} onChange={set("title")} /></Field>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Categoria"><input data-testid="regulation-category-input" className="nx-input" value={f.category} onChange={set("category")} /></Field>
            <Field label="Versione"><input data-testid="regulation-version-input" className="nx-input" value={f.version} onChange={set("version")} /></Field>
            <Field label="Stato"><select data-testid="regulation-status-select" className="nx-input" value={f.status} onChange={set("status")}><option value="published">Pubblicato</option><option value="draft">Bozza</option><option value="archived">Archiviato</option></select></Field>
          </div>
          <Field label="Testo"><textarea data-testid="regulation-content-input" required rows={12} className="nx-input" value={f.content} onChange={set("content")} /></Field>
          <div className="flex justify-end gap-2"><button type="button" onClick={onClose} className="btn-ghost btn-sm">Annulla</button><button data-testid="regulation-form-submit" className="btn-primary btn-sm">Salva</button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function AdminRegulations() {
  const { user } = useAuth();
  const isSuper = user.role === "super_admin";
  const [editing, setEditing] = useState(undefined);
  const { data, loading, error, reload } = useFetch("/admin/regulations");
  return (
    <div data-testid="dash-regulations">
      <DashHeader title="Regolamenti" subtitle={isSuper ? "Regolamenti ufficiali pubblicati sul sito." : "Consultazione. La modifica è riservata al Super Admin."}>
        {isSuper && <button data-testid="new-regulation-button" onClick={() => setEditing(null)} className="btn-primary btn-sm"><Plus className="w-4 h-4" /> Nuovo regolamento</button>}
      </DashHeader>
      {error ? <ErrorState message={error} onRetry={reload} /> : loading && !data ? <Loader /> : data.length ? (
        <div className="nx-card divide-y divide-[#C6C6CE]/5">
          {data.map((r) => (
            <div key={r.id} className="p-4 flex items-center gap-4" data-testid={`admin-regulation-row-${r.slug}`}>
              <div className="flex-1"><div className="text-[#F5F5F7]">{r.title}</div><div className="text-xs text-[#C6C6CE]/50">{r.category} · v{r.version} · {r.status}</div></div>
              {isSuper && <button data-testid={`edit-regulation-${r.slug}`} onClick={() => setEditing(r)} className="btn-ghost btn-sm"><Pencil className="w-3.5 h-3.5" /></button>}
            </div>
          ))}
        </div>
      ) : <EmptyState title="Nessun regolamento" />}
      {editing !== undefined && <RegulationDialog item={editing} onClose={() => setEditing(undefined)} onSaved={reload} />}
    </div>
  );
}

function PageEditor({ page, onSaved }) {
  const [f, setF] = useState({ title: page.title, content: page.content || "" });
  const save = async () => {
    try { await api.put(`/admin/pages/${page.key}`, f); toast.success(`"${f.title}" salvato`); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="nx-card p-5 space-y-3" data-testid={`page-editor-${page.key}`}>
      <input className="nx-input font-display uppercase" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} aria-label="Titolo sezione" />
      <textarea data-testid={`page-content-${page.key}`} rows={5} className="nx-input" value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} placeholder="Testo della sezione" />
      <button data-testid={`page-save-${page.key}`} onClick={save} className="btn-primary btn-sm">Salva sezione</button>
    </div>
  );
}

const SOC = ["discord", "instagram", "youtube", "twitch", "x", "tiktok"];

function SettingsEditor({ settings, onSaved }) {
  const [f, setF] = useState({ tagline: settings.tagline || "", email: settings.email || "", socials: { ...Object.fromEntries(SOC.map((k) => [k, ""])), ...(settings.socials || {}) } });
  const save = async () => {
    try { await api.put("/admin/settings", f); toast.success("Impostazioni salvate"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="nx-card p-5 space-y-4" data-testid="settings-editor">
      <div className="eyebrow">Contatti e collegamenti ufficiali</div>
      <Field label="Presentazione breve (hero e footer)"><input data-testid="settings-tagline-input" className="nx-input" value={f.tagline} onChange={(e) => setF({ ...f, tagline: e.target.value })} /></Field>
      <Field label="Email di contatto"><input data-testid="settings-email-input" className="nx-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
      <div className="grid sm:grid-cols-3 gap-3">{SOC.map((k) => <Field key={k} label={k}><input data-testid={`settings-social-${k}`} className="nx-input" value={f.socials[k]} onChange={(e) => setF({ ...f, socials: { ...f.socials, [k]: e.target.value } })} /></Field>)}</div>
      <button data-testid="settings-save" onClick={save} className="btn-primary btn-sm">Salva impostazioni</button>
    </div>
  );
}

export function AdminContent() {
  const { data, loading, error, reload } = useFetch("/site");
  if (loading && !data) return <Loader />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  return (
    <div data-testid="dash-content">
      <DashHeader title="Contenuti alleanza" subtitle="Tutti i testi istituzionali sono modificabili senza intervenire sul codice." />
      <div className="space-y-4">
        <SettingsEditor settings={data.settings || {}} onSaved={reload} />
        {data.pages.map((p) => <PageEditor key={p.key} page={p} onSaved={reload} />)}
      </div>
    </div>
  );
}
